import { useCallback, useEffect, useRef, useState } from "react";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../firebase.web";

/**
 * Image load fallback mirroring components/Static.js VLImage handleError (:84-101): on error with a
 * firebasestorage URL, retry once through getDownloadURL(ref(storage, path)), then give up.
 * Returns { src, failed, onError }; `failed` means render the placeholder instead of the image.
 */
export default function useImageFallback(url) {
  const [state, setState] = useState({ url, src: url, retried: false, failed: false });
  const cur = state.url === url ? state : { url, src: url, retried: false, failed: false };
  const urlRef = useRef(url);
  useEffect(() => { urlRef.current = url; }, [url]);

  const onError = useCallback(async () => {
    if (!cur.retried && typeof url === "string" && url.includes("firebasestorage.googleapis.com")) {
      setState({ url, src: url, retried: true, failed: false });
      try {
        const path = decodeURIComponent(url.split("/o/")[1]?.split("?")[0] || "");
        if (path) {
          const signed = await getDownloadURL(ref(storage, path));
          if (urlRef.current === url) setState({ url, src: signed, retried: true, failed: false });
          return;
        }
      } catch {
        /* fall through to the placeholder */
      }
    }
    if (urlRef.current === url) setState({ url, src: cur.src, retried: true, failed: true });
  }, [url, cur.retried, cur.src]);

  return { src: cur.src, failed: cur.failed || !url, onError };
}
